import "./wdyr";
import React from "react";
import { trackException } from "functions/telemetry";
import { v1 as uuidv1 } from "uuid";
import ms from "ms";
import AuthPageContainer from "./containers/AuthPageContainer";
import { Box, Link, Typography } from "@mui/material";
import type {
  BrowserInfo,
  SearchBotDeviceInfo,
  BotInfo,
  NodeInfo,
  ReactNativeInfo,
} from "detect-browser";

type BrowserType = BrowserInfo | SearchBotDeviceInfo | BotInfo | NodeInfo | ReactNativeInfo | null;

interface ErrorBoundaryProps {
  browserType?: BrowserType;
  submitted?: boolean;
  onSubmit?: () => void;
  children?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  HasError?: boolean;
  errorMessage: string | Error;
  countdown: number;
  browserType?: BrowserType;
  uuid: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: "",
      countdown: 3,
      browserType: props.browserType,

      uuid: uuidv1(),
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, errorMessage: error };
  }

  componentDidCatch() {
    if (this.isDynamicLoadError()) {
      setInterval(() => {
        if (this.state.countdown === 1) {
          localStorage.setItem("autoRestartFromIndex", String(Date.now()));
          globalThis.location.reload();
        } else {
          this.setState((previousState) => ({
            countdown: previousState.countdown - 1,
          }));
        }
      }, 1000);
    }
    this.setState({
      HasError: true,
    });
  }

  isDynamicLoadError = (): boolean => {
    const storedTimestamp = localStorage.getItem("autoRestartFromIndex");
    const errorMessage = this.state.errorMessage.toString().toLowerCase();

    if (storedTimestamp) {
      const currentTimestamp = Date.now();

      if (currentTimestamp - Number(storedTimestamp) < ms("5m")) return false;
    }

    const importModuleErrors = [
      "failed to fetch dynamically imported module",
      "failed to asynchronously load component",
      "failed to load module script",
      "error loading dynamically imported module",
    ];
    return importModuleErrors.some((err) => errorMessage.includes(err));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.submitted) {
        trackException(
          this.state.uuid,
          this.state.errorMessage.toString(),
          this.props.browserType ?? null,
          undefined
        );
        this.props.onSubmit?.();
      }

      return (
        <AuthPageContainer>
          {this.isDynamicLoadError() ? (
            <Box sx={{ mt: 1.25, textAlign: "center" }}>
              <h2>A new version is available</h2>
              <hr />
              <p>
                The application will reload to get the recent changes. Restart
                in: {this.state.countdown}
              </p>
            </Box>
          ) : (
            <Typography
              component="div"
              sx={{ mt: 1.25, textAlign: "center" }}
            >
              <Typography component="p">
                This error has been logged and reported with an ID of{" "}
                {this.state.uuid}.
              </Typography>
              <hr />
              <Typography component="p">
                You can also try to{" "}
                <Link underline={"none"} href="/logout">
                  log out
                </Link>{" "}
                and log back in.
              </Typography>
            </Typography>
          )}
        </AuthPageContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
